import { Component, signal, inject, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter, firstValueFrom } from 'rxjs';
import { GPTAssistantService, ChatMessage } from '../../services/gpt-assistant.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-gpt-assistant',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gpt-assistant.html',
  styleUrl: './gpt-assistant.css'
})
export class GPTAssistantComponent implements OnInit, OnDestroy, AfterViewChecked {
  private gptService = inject(GPTAssistantService);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

  isOpen = signal(false);
  isVisible = signal(true);
  messages = signal<ChatMessage[]>([]);
  currentMessage = '';
  isLoading = signal(false);
  error = signal<string | null>(null);
  userPlan = this.authService.userPlan;

  private routerSubscription: any;
  private shouldScroll = false;

  ngOnInit() {
    // Verificar plan del usuario
    this.checkPlanAccess();

    // Verificar si estamos en la ruta del chat entre alumnos
    this.checkRoute();

    // Suscribirse a cambios de ruta
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkRoute();
        this.checkPlanAccess();
      });
  }

  /**
   * Verificar acceso según el plan del usuario
   * Solo visible para planes diferentes de id_plan 4
   */
  private checkPlanAccess() {
    const plan = this.userPlan();
    if (!plan || plan.id_plan === 4) {
      this.isVisible.set(false);
      this.isOpen.set(false);
    } else {
      // Solo mostrar si no estamos en el chat entre alumnos
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/community/chat')) {
        this.isVisible.set(true);
      }
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Verificar la ruta actual y ocultar el asistente en el chat entre alumnos
   */
  private checkRoute() {
    const currentUrl = this.router.url;
    const plan = this.userPlan();

    // Ocultar en la ruta del chat entre alumnos o si no tiene plan válido
    if (currentUrl.includes('/community/chat') || !plan || plan.id_plan === 4) {
      this.isVisible.set(false);
      this.isOpen.set(false);
    } else {
      this.isVisible.set(true);
    }
  }

  /**
   * Toggle del asistente
   */
  async toggleAssistant() {
    if (!this.isVisible()) return;

    const wasOpen = this.isOpen();
    this.isOpen.update(open => !open);

    // Si se abre, cargar conversación activa o mostrar bienvenida
    if (this.isOpen() && !wasOpen) {
      await this.loadActiveConversation();
    }
  }

  /**
   * Cerrar el asistente
   */
  closeAssistant() {
    this.isOpen.set(false);
  }

  /**
   * Agregar mensaje de bienvenida
   */
  private addWelcomeMessage() {
    const welcomeMessage: ChatMessage = {
      role: 'assistant',
      content: '¡Hola! Soy tu asistente educativo. ¿En qué puedo ayudarte hoy? Puedes preguntarme sobre los cursos, conceptos, ejercicios o cualquier tema relacionado con tu aprendizaje.',
      timestamp: new Date(),
      isNew: false // No animar el mensaje de bienvenida al cargar
    };
    this.messages.update(msgs => [...msgs, welcomeMessage]);
  }

  /**
   * Enviar mensaje
   */
  async sendMessage() {
    const message = this.currentMessage.trim();
    if (!message || this.isLoading()) return;

    // Guardar el mensaje del usuario temporalmente
    const userMessageText = message;
    this.currentMessage = '';
    this.isLoading.set(true);
    this.error.set(null);

    try {
      // Obtener ID de conversación actual si existe
      const conversationId = this.gptService.getCurrentConversationId();

      // Enviar mensaje al servicio
      const response = await firstValueFrom(
        this.gptService.sendMessage(userMessageText, conversationId)
      );

      if (response.ok && response.mensaje_asistente) {
        // Procesar respuesta
        const processed = this.gptService.processChatResponse(response);

        // Actualizar conversación actual si se creó una nueva
        if (processed.conversationId) {
          this.gptService.setCurrentConversationId(processed.conversationId);
        }

        // Agregar mensaje del usuario (usar el del backend si está disponible, sino el local)
        const userMessage: ChatMessage = processed.userMessage.content
          ? processed.userMessage
          : {
            role: 'user',
            content: userMessageText,
            timestamp: new Date()
          };

        // Agregar mensajes a la UI con flag de nuevo mensaje
        const userMessageWithFlag: ChatMessage = {
          ...userMessage,
          isNew: true
        };
        this.messages.update(msgs => [...msgs, userMessageWithFlag]);
        // Scroll inmediato después de agregar mensaje del usuario
        this.shouldScroll = true;
        setTimeout(() => this.scrollToBottom(), 50);

        // Remover flag después de la animación
        setTimeout(() => {
          this.messages.update(msgs =>
            msgs.map(msg => msg === userMessageWithFlag ? { ...msg, isNew: false } : msg)
          );
        }, 500);

        // Agregar respuesta del asistente con animación
        setTimeout(() => {
          const assistantMessage: ChatMessage = {
            ...processed.assistantMessage,
            timestamp: processed.assistantMessage.timestamp || new Date(),
            isNew: true
          };
          this.messages.update(msgs => [...msgs, assistantMessage]);
          // Scroll al final después de agregar el mensaje del asistente
          this.shouldScroll = true;
          setTimeout(() => this.scrollToBottom(), 150);

          // Remover flag después de la animación
          setTimeout(() => {
            this.messages.update(msgs =>
              msgs.map(msg => msg === assistantMessage ? { ...msg, isNew: false } : msg)
            );
          }, 500);
        }, 300);
      } else {
        throw new Error(response.msg || 'No se recibió respuesta del asistente');
      }
    } catch (err: any) {
      console.error('Error al enviar mensaje:', err);
      const errorMsg = err.error?.msg || err.message || 'Error al comunicarse con el asistente. Por favor, intenta de nuevo.';
      this.error.set(errorMsg);

      // Agregar mensaje del usuario de todas formas
      const userMessage: ChatMessage = {
        role: 'user',
        content: userMessageText,
        timestamp: new Date(),
        isNew: true
      };
      this.messages.update(msgs => [...msgs, userMessage]);
      this.shouldScroll = true;
      setTimeout(() => this.scrollToBottom(), 50);

      // Remover flag después de la animación
      setTimeout(() => {
        this.messages.update(msgs =>
          msgs.map(msg => msg === userMessage ? { ...msg, isNew: false } : msg)
        );
      }, 500);

      // Agregar mensaje de error
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, verifica tu conexión e intenta de nuevo.',
        timestamp: new Date(),
        isNew: true
      };
      this.messages.update(msgs => [...msgs, errorMessage]);
      this.shouldScroll = true;
      setTimeout(() => this.scrollToBottom(), 100);

      // Remover flag después de la animación
      setTimeout(() => {
        this.messages.update(msgs =>
          msgs.map(msg => msg === errorMessage ? { ...msg, isNew: false } : msg)
        );
      }, 500);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Enviar mensaje con Enter
   */
  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Getter para el mensaje actual (para ngModel)
   */
  get messageValue(): string {
    return this.currentMessage;
  }

  /**
   * Setter para el mensaje actual (para ngModel)
   */
  set messageValue(value: string) {
    this.currentMessage = value;
  }

  /**
   * Cargar conversación activa
   */
  private async loadActiveConversation() {
    try {
      // Obtener conversaciones activas
      const conversacionesResponse = await firstValueFrom(
        this.gptService.obtenerConversaciones(1, 1, true)
      );

      if (conversacionesResponse.ok && conversacionesResponse.conversaciones && conversacionesResponse.conversaciones.length > 0) {
        // Cargar la conversación más reciente
        const ultimaConversacion = conversacionesResponse.conversaciones[0];
        const mensajes = await this.gptService.cargarConversacion(ultimaConversacion.id_conversacion);

        if (mensajes.length > 0) {
          // Convertir mensajes del backend al formato del componente (sin animación porque son mensajes cargados)
          const mensajesSinAnimacion = mensajes.map(msg => ({ ...msg, isNew: false }));
          this.messages.set(mensajesSinAnimacion);
        } else {
          this.addWelcomeMessage();
        }
      } else {
        // No hay conversaciones activas, mostrar bienvenida
        this.addWelcomeMessage();
      }
    } catch (error) {
      console.error('Error al cargar conversación activa:', error);
      this.addWelcomeMessage();
    }
  }

  /**
   * Limpiar conversación
   */
  async clearConversation() {
    try {
      // Crear nueva conversación en el backend
      const response = await firstValueFrom(this.gptService.crearNuevaConversacion());

      if (response.ok) {
        // Limpiar mensajes locales
        this.messages.set([]);
        this.gptService.clearCurrentConversation();

        // Establecer nueva conversación si se creó
        if (response.id_conversacion) {
          this.gptService.setCurrentConversationId(response.id_conversacion);
        }

        // Mostrar mensaje de bienvenida
        this.addWelcomeMessage();
      } else {
        throw new Error(response.msg || 'Error al crear nueva conversación');
      }
    } catch (error) {
      console.error('Error al limpiar conversación:', error);
      // Limpiar localmente de todas formas
      this.messages.set([]);
      this.gptService.clearCurrentConversation();
      this.addWelcomeMessage();
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  /**
   * Scroll al final del chat
   */
  private scrollToBottom() {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Fallback si no está disponible el ViewChild
      const chatContainer = document.querySelector('.gpt-chat-messages');
      if (chatContainer) {
        chatContainer.scrollTo({
          top: chatContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  }

  /**
   * Convertir markdown básico a HTML
   */
  parseMarkdown(text: string): SafeHtml {
    if (!text) return this.sanitizer.sanitize(1, '') as SafeHtml;

    let html = text;

    // Primero proteger código inline antes de procesar otras cosas
    const codeBlocks: string[] = [];
    html = html.replace(/`([^`]+)`/g, (match, code) => {
      const placeholder = `__CODE_${codeBlocks.length}__`;
      codeBlocks.push(code);
      return placeholder;
    });

    // Negritas: **texto** o __texto__
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(?!CODE)(.*?)(?<!CODE)__/g, '<strong>$1</strong>');

    // Restaurar código inline
    codeBlocks.forEach((code, index) => {
      html = html.replace(`__CODE_${index}__`, `<code>${code}</code>`);
    });

    // Dividir en párrafos (doble salto de línea)
    const paragraphs = html.split(/\n\n+/);
    html = paragraphs.map(para => {
      para = para.trim();
      if (!para) return '';

      // Procesar listas dentro del párrafo
      // Listas numeradas: 1. item
      if (/^\d+\.\s+/.test(para)) {
        const items = para.split(/\n(?=\d+\.\s+)/);
        if (items.length > 1) {
          const listItems = items.map(item => {
            const match = item.match(/^\d+\.\s+(.+)$/s);
            return match ? `<li>${match[1].trim()}</li>` : '';
          }).filter(Boolean).join('');
          return `<ol>${listItems}</ol>`;
        }
      }

      // Listas con viñetas: - item o * item (pero no si es negrita)
      if (/^[-*]\s+/.test(para) && !para.startsWith('**')) {
        const items = para.split(/\n(?=^[-*]\s+)/m);
        if (items.length > 1) {
          const listItems = items.map(item => {
            const match = item.match(/^[-*]\s+(.+)$/s);
            return match ? `<li>${match[1].trim()}</li>` : '';
          }).filter(Boolean).join('');
          return `<ul>${listItems}</ul>`;
        }
      }

      // Convertir saltos de línea simples a <br>
      para = para.replace(/\n/g, '<br>');

      // Si no es una lista, envolver en párrafo
      if (!para.startsWith('<ol>') && !para.startsWith('<ul>')) {
        return `<p>${para}</p>`;
      }

      return para;
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Formatear fecha del mensaje
   */
  formatTime(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
